import type { ChatReactionSummary } from "@xop/dashboard-shared";

/** Names past this are collapsed into "and N others" so the hover card stays
 * one readable line even on a heavily-reacted room message. */
const MAX_NAMES = 6;

/**
 * "Who reacted with this emoji" — the hover label on a reaction chip.
 *
 * The viewer is always named "You" and listed FIRST (WhatsApp/Slack habit), so
 * you can tell at a glance whether the chip counts your own tap. Names come
 * from the server (`user_names`, index-aligned with `user_ids`); when they're
 * missing — an older payload, or a reactor the server couldn't name — the
 * label degrades to a plain count rather than showing a blank entry.
 */
export function reactionTooltip(r: ChatReactionSummary, selfId: number): string {
  const names = r.user_names ?? [];
  const mine = r.user_ids.includes(selfId);
  const others: string[] = [];
  r.user_ids.forEach((id, i) => {
    if (id === selfId) return;
    const name = names[i]?.trim();
    if (name) others.push(name);
  });

  // Reactors we have no name for (short/absent user_names) — counted, not named.
  const unnamed = r.user_ids.length - others.length - (mine ? 1 : 0);
  const listed = mine ? ["You", ...others] : [...others];
  const overflow = Math.max(0, listed.length - MAX_NAMES) + Math.max(0, unnamed);
  const shown = listed.slice(0, MAX_NAMES);

  if (shown.length === 0) {
    return `${r.count} ${r.count === 1 ? "reaction" : "reactions"} ${r.emoji}`;
  }
  const parts = [...shown];
  if (overflow > 0) parts.push(`${overflow} ${overflow === 1 ? "other" : "others"}`);
  return `${joinNames(parts)} reacted ${r.emoji}`;
}

/** "A" · "A and B" · "A, B and C" — an Oxford-comma-free English list. */
function joinNames(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  const last = parts[parts.length - 1]!;
  return `${parts.slice(0, -1).join(", ")} and ${last}`;
}
